import { useQuery } from '@tanstack/react-query';
import { cn } from 'cn';
import { Skeleton } from '@/components/ui/skeleton';
import { toApiError } from '@/lib/http';

/**
 * The document card: a khutbah in one screen.
 *
 * It has two halves and they are **not equally real**, which is the whole
 * difficulty of this pane:
 *
 * - **The quotations are extracted fact** — which passages quote Qur'an or
 *   hadith, detected from the document, carrying whether a person confirmed
 *   the reference.
 * - **The summary and lessons are generated**, and nothing generates them
 *   yet. A lesson a model drew out of a khutbah is not the khutbah's own
 *   claim, so the card must not blur the two even once it does exist.
 *
 * An unconfirmed match never reads as a settled reference. The schema
 * enforces it too — an `unverified` row cannot carry a surah — but the
 * interface says it in words, because a reader cannot see a constraint.
 */

interface Quotation {
  readonly passageOrdinal: number;
  readonly kind: 'quran' | 'hadith';
  readonly reference: string | null;
  readonly referenceAsWritten: string | null;
  readonly matchMethod: string;
  readonly confirmed: boolean;
  readonly arabic: string | null;
}

interface Card {
  readonly quotations: readonly Quotation[];
  readonly generated: { summary: string | null; status: string } | null;
}

function parseCard(value: unknown): Card {
  const source = (typeof value === 'object' && value !== null ? value : {}) as Record<
    string,
    unknown
  >;
  const generated = source.generated;
  return {
    quotations: (Array.isArray(source.quotations) ? source.quotations : []).flatMap(
      (entry): Quotation[] => {
        if (typeof entry !== 'object' || entry === null) return [];
        const row = entry as Record<string, unknown>;
        return [
          {
            passageOrdinal:
              typeof row.passageOrdinal === 'number' ? row.passageOrdinal : 0,
            kind: row.kind === 'hadith' ? 'hadith' : 'quran',
            reference: typeof row.reference === 'string' ? row.reference : null,
            referenceAsWritten:
              typeof row.referenceAsWritten === 'string'
                ? row.referenceAsWritten
                : null,
            matchMethod:
              typeof row.matchMethod === 'string' ? row.matchMethod : 'unverified',
            // Defaults to false. Claiming a reference is confirmed when the
            // server did not say so is the one direction that must never slip.
            confirmed: row.confirmed === true,
            arabic: typeof row.arabic === 'string' ? row.arabic : null,
          },
        ];
      },
    ),
    generated:
      typeof generated === 'object' && generated !== null
        ? {
            summary:
              typeof (generated as Record<string, unknown>).summary === 'string'
                ? ((generated as Record<string, unknown>).summary as string)
                : null,
            status:
              typeof (generated as Record<string, unknown>).status === 'string'
                ? ((generated as Record<string, unknown>).status as string)
                : 'ai_draft',
          }
        : null,
  };
}

export function DocumentCard({
  khutbahId,
  language,
  sourceScript,
}: {
  khutbahId: string;
  language: string;
  sourceScript: string;
}) {
  const card = useQuery({
    queryKey: ['khutbah', khutbahId, 'card', language],
    queryFn: async ({ signal }) => {
      const response = await fetch(
        `/api/public/khutbah/${encodeURIComponent(khutbahId)}/card?language=${encodeURIComponent(language)}`,
        { signal, headers: { Accept: 'application/json' } },
      );
      if (!response.ok) throw await toApiError(response);
      return parseCard(await response.json());
    },
    retry: false,
  });

  if (card.isPending) return <Skeleton className="h-40" />;
  if (card.error) {
    return (
      <p role="alert" className="rounded-lg border border-destructive p-4 text-sm">
        Could not load the card.
      </p>
    );
  }

  const quran = card.data.quotations.filter((q) => q.kind === 'quran');
  const hadith = card.data.quotations.filter((q) => q.kind === 'hadith');
  const unconfirmed = card.data.quotations.filter((q) => !q.confirmed).length;

  return (
    <div className="flex flex-col gap-5">
      <section>
        <h2 className="text-sm font-semibold">Summary</h2>
        {card.data.generated?.summary ? (
          <>
            <p className="mt-1 text-sm text-pretty">{card.data.generated.summary}</p>
            {/* A summary is the model's reading of the khutbah, not the
                khutbah's own words, and it says so every time it appears. */}
            <p className="mt-1 text-xs text-muted-foreground">
              Written by a model from the khutbah's text
              {card.data.generated.status === 'published'
                ? ' and approved by a reviewer.'
                : ', and not yet checked by a reviewer.'}
            </p>
          </>
        ) : (
          <p className="mt-1 rounded-md border border-dashed border-border p-3 text-[13px] text-muted-foreground">
            No summary yet. Summaries, key lessons and suggested questions are
            generated from the khutbah, and that step is not built — so there
            is nothing here rather than something invented.
          </p>
        )}
      </section>

      <section>
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold">
            Qur’an quoted{quran.length > 0 ? ` (${quran.length})` : ''}
          </h2>
          {unconfirmed > 0 ? (
            <span className="text-xs text-muted-foreground">
              {unconfirmed} not confirmed
            </span>
          ) : null}
        </div>

        {quran.length === 0 && hadith.length === 0 ? (
          <p className="mt-1 text-[13px] text-muted-foreground">
            No quotations were identified. Plain text carries no font
            information, so for some sources they cannot be identified at all —
            which is different from there being none.
          </p>
        ) : (
          <ul className="mt-2 flex flex-col gap-2">
            {[...quran, ...hadith].map((quotation, index) => (
              <li
                key={`${quotation.passageOrdinal}-${index}`}
                className="rounded-md border border-quote-arabic-border bg-quote-arabic-bg p-3"
              >
                <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="rounded-sm bg-citation-bg px-1.5 py-0.5 font-semibold text-citation-foreground">
                    {quotation.kind === 'quran' ? 'Qur’an' : 'Hadith'}
                  </span>
                  {/* The reference exists only when matching produced one.
                      Never built from the text. */}
                  {quotation.reference ? (
                    <span className="font-medium">{quotation.reference}</span>
                  ) : null}
                  <span
                    className={cn(
                      'italic',
                      quotation.confirmed ? 'text-success' : 'text-muted-foreground',
                    )}
                  >
                    {quotation.confirmed
                      ? '· confirmed by an editor'
                      : quotation.reference
                        ? '· detected by matching, not confirmed'
                        : '· reference not identified'}
                  </span>
                  <span className="text-muted-foreground">
                    · passage {quotation.passageOrdinal + 1}
                  </span>
                </div>

                {quotation.arabic ? (
                  <p
                    lang="ar"
                    dir="rtl"
                    className={cn(sourceScript, 'font-normal text-reading-foreground')}
                  >
                    {quotation.kind === 'quran' ? (
                      <>
                        <span className="text-citation-foreground" aria-hidden>
                          ﴿
                        </span>{' '}
                        {quotation.arabic}{' '}
                        <span className="text-citation-foreground" aria-hidden>
                          ﴾
                        </span>
                      </>
                    ) : (
                      quotation.arabic
                    )}
                  </p>
                ) : (
                  <p className="text-[13px] text-muted-foreground">
                    Quoted in a Qur’an font whose glyphs are not decoded, so it
                    is not shown rather than shown wrongly.
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-md border border-dashed border-border p-3">
        <h2 className="text-sm font-semibold">Not built yet</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Key lessons and suggested questions come from the same generation
          step as the summary. Nothing produces them, so they are absent rather
          than empty.
        </p>
      </section>
    </div>
  );
}
